import cs from "classnames";
import React from "react";
import { FaTimes } from "react-icons/fa";
import { defineMessages, FormattedMessage } from "react-intl";

import {
	DEFAULT_TIME,
	formatDateTimeValue,
	getDateStr,
	parseDateTimeValue
} from "src/common/utils/dates";

import "./DateTimeInput.css";

export const messages = defineMessages({
	today: {
		id: "timestamp_input.today",
		defaultMessage: "Ma"
	}
});

export interface TimestampInputProps {
	disabled?: boolean;
	value?: string;
	onChangeValue?(value: string | undefined): unknown;
}

const noop = (): void => {};

export const TimestampInput: React.FunctionComponent<TimestampInputProps> = ({
	disabled,
	value,
	onChangeValue = noop
}) => {
	const [isDateFocused, setDateFocused] = React.useState(false);
	const [isClearFocused, setClearFocused] = React.useState(false);
	const [isNowFocused, setNowFocused] = React.useState(false);

	const { date, time } = React.useMemo(
		() => parseDateTimeValue(value),
		[value]
	);

	const onFocusDate = React.useCallback(() => {
		setDateFocused(true);
	}, []);

	const onBlurDate = React.useCallback(() => {
		setDateFocused(false);
	}, []);

	const onFocusClear = React.useCallback(() => {
		setClearFocused(true);
	}, []);

	const onBlurClear = React.useCallback(() => {
		setClearFocused(false);
	}, []);

	const onFocusNow = React.useCallback(() => {
		setNowFocused(true);
	}, []);

	const onBlurNow = React.useCallback(() => {
		setNowFocused(false);
	}, []);

	const onChangeDate = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChangeValue(
				formatDateTimeValue(event.target.value, DEFAULT_TIME)
			);
		},
		[onChangeValue]
	);

	const onClickClear = React.useCallback(() => {
		onChangeValue(undefined);
	}, [onChangeValue]);

	const onClickNow = React.useCallback(() => {
		onChangeValue(
			formatDateTimeValue(getDateStr(new Date()), DEFAULT_TIME)
		);
	}, [onChangeValue]);

	return (
		<div
			className={cs("date-time", {
				"date-time--disabled": disabled,
				"date-time--focused":
					isDateFocused || isClearFocused || isNowFocused
			})}
		>
			<input
				className="date-time__input date-time__input--date"
				disabled={disabled}
				type="date"
				max="9999-12-31"
				value={date}
				onFocus={onFocusDate}
				onBlur={onBlurDate}
				onChange={onChangeDate}
			/>
			{time !== "" && time !== DEFAULT_TIME && (
				<input
					className="date-time__input date-time__input--time"
					disabled={true}
					type="time"
					step="1"
					value={time}
					onChange={noop}
				/>
			)}
			<button
				disabled={disabled || !value}
				className="date-time__button date-time__button--clear"
				type="button"
				onFocus={onFocusClear}
				onBlur={onBlurClear}
				onClick={onClickClear}
			>
				<FaTimes />
			</button>
			<button
				disabled={disabled}
				className="date-time__button date-time__button--now"
				type="button"
				onFocus={onFocusNow}
				onBlur={onBlurNow}
				onClick={onClickNow}
			>
				<FormattedMessage {...messages.today} />
			</button>
		</div>
	);
};
